import { useState } from "react";
import { useDebounce } from "./useDebounce";
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";

interface UsePaginatedResourceParams<TFilters extends Record<string, any>> {
  initialFilters?: TFilters;
  initialPageSize?: number;
  debounceDelay?: number;
}

/**
 * usePaginatedResource
 * Generic, reusable hook for managing pagination and filters (with debounce).
 */
export const usePaginatedResource = <TFilters extends Record<string, any>>({
  initialFilters = {} as TFilters,
  initialPageSize = DEFAULT_PAGE_SIZE,
  debounceDelay = 400,
}: UsePaginatedResourceParams<TFilters>) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState<TFilters>(initialFilters);

  const debouncedFilters = useDebounce(filters, debounceDelay);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleFilterChange = <K extends keyof TFilters>(
    key: K,
    value: TFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  return {
    page,
    pageSize,
    filters,
    debouncedFilters,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    resetFilters,
  };
};
